import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Modal, Table, InputNumber, Button, Flex, notification, Tag, Typography, Empty, Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { OrderItemV2, OrderAddItemV2, InventoryLocationItem } from '@/types/inventoryV2';
import { productLocations } from '@/apis/inventoryv2';
import STableLayout from '@/styles/STableLayout';
import SText from '@/styles/SText';
import { CommonListParams } from '@/types/common';
import { MaterialType } from '@/pages/Warehouse/pages/InventoryManage/components/MaterialDetail';
import { getLocationIdx } from '@/utils/wLocation';
import SelectLocationType from '@/components/Selector/SelectLocationType';
import { DictContext } from '@/layout/context/DictContext';

interface FixOutBoundItemsProps {
  errItems: Array<OrderItemV2>;
  open: boolean;
  onClose: () => void;
  onConfirm: (adds: Array<OrderAddItemV2>) => void;
  isReturn?: boolean;
}

interface InventoryLocationItemWithOutCount extends InventoryLocationItem {
  outCount: number;
}

const FixOutBoundItems: React.FC<FixOutBoundItemsProps> = ({
  errItems,
  open,
  onClose,
  onConfirm,
  isReturn = false
}) => {
  const { t } = useTranslation();
  const { locationTypes } = useContext(DictContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [locations, setLocations] = useState<InventoryLocationItemWithOutCount[]>([]);
  const [locTypes, setLocTypes] = useState<number[]>([1]);

  useEffect(() => {
    if (open && errItems.length > 0) {
      fetchProductLocations();
    } else {
      setLocations([]);
    }
  }, [open, errItems]);

  const showData = useMemo(() => {
    if (!!!locTypes?.length) return [...locations]
    else return locations.filter(loc => locTypes.includes(loc.location.type))
  }, [locations, locTypes]);

  const outQty = useMemo(() => {
    return showData.reduce((sum, loc) => sum + loc.outCount, 0)
  }, [showData]);

  const fetchProductLocations = async () => {
    setLoading(true);
    try {
      const proId = errItems[0]?.productId;
      if (!proId) return;

      // const params: CommonListParams = {
      //   pagination: { offset: 0, take: 1000 }
      // };
      const res = await productLocations(proId, 'COMMON' as MaterialType);
      let dataWithOutCount = (res.body || [])
        .filter(item => item.count > 0)
        .map(item => ({
          ...item,
          outCount: 0
        }));
      if (isReturn) dataWithOutCount = dataWithOutCount.filter(loc => loc.lotItem?.lot?.lot === errItems[0].lot);
      if (dataWithOutCount.length === 0) notification.error({
        message: t('warehouse.noUseStock'),
        description: isReturn ? t('warehouse.noLotStock', { lot: errItems[0].lot || '' }) : t('warehouse.noCodeStock', { code: errItems[0]?.product?.code || '' })
      })
      setLocations([...dataWithOutCount])
    } catch (error) {
      notification.error({
        message: t('warehouse.fetchStockError'),
      });
    } finally {
      setLoading(false);
    }
  };

  // errItems 表格列
  const errItemColumns: ColumnsType<OrderItemV2> = useMemo(() => [
    {
      title: t('warehouse.batchNo'),
      dataIndex: 'lot',
      key: 'lot',
      width: 240,
      render: (text) => <SText text={text || ''} />
    },
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 100,
          render: (_, record) => <SText text={record.product?.code || ''} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} />
        },
        {
          title: <SText text={t('product.brand1')} />,
          key: 'model',
          width: 200,
          render: (_, record) => <SText text={record.product?.model || ''} />
        },
      ]
    },
    {
      title: <SText text={t('warehouse.location')} />,
      key: 'locations',
      width: 180,
      render: (_, record) => {
        if (!record.locations || record.locations.length === 0) {
          return '-';
        }
        return <Flex wrap>
          {record.locations.map(loc => (
            <Tag style={{ marginBottom: 4 }}>{getLocationIdx(loc.location)} ({loc.count})</Tag>
          ))}
        </Flex>
      }
    },
    {
      title: t('warehouse.outQuantity'),
      dataIndex: 'count',
      key: 'count',
      width: 100,
      render: (value) => value || 0
    },
  ], [t]);

  // locations 表格列
  const locationColumns: ColumnsType<InventoryLocationItemWithOutCount> = useMemo(() => [
    {
      title: t('warehouse.batchNo'),
      dataIndex: 'lot',
      key: 'lot',
      width: 170,
      align: 'center',
      render: (_, record) => <SText text={record.lotItem.lot.lot || ''} />
    },
    {
      title: t('warehouse.location'),
      key: 'location',
      width: 120,
      align: 'center',
      render: (_, record) => <SText text={getLocationIdx(record.location)} />
    },
    {
      title: t('inventory.locationType'),
      key: 'locationType',
      width: 120,
      align: 'center',
      render: (_, record) => {
        let info = locationTypes.find(label => label.key === record.location.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t('bom.useStock'),
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'center',
      render: (_, record) => <SText text={(record.count || 0) - (record.preOut) + ''} />
    },
    {
      title: t('warehouse.outQuantity'),
      key: 'outCount',
      width: 100,
      fixed: "right",
      align: 'center',
      render: (_, record, index) => (
        <InputNumber changeOnWheel
          min={0}
          max={(record.count || 0) - (record.preOut)}
          precision={0}
          value={record.outCount}
          onChange={(value) => {
            const newLocations = [...locations];
            newLocations[index].outCount = value ?? 0;
            setLocations(newLocations);
          }}
          style={{ width: '100%' }}
        />
      )
    },
  ], [t, locations]);

  const handleConfirm = () => {
    const errItem = errItems[0];

    if (outQty !== errItem.count) {
      notification.error({
        message: t('warehouse.outQtyErr')
      });
      return;
    }

    // 直接遍历 outLocations 构造 OrderAddItemV2 数据
    const outLocations = showData.filter(loc => loc.outCount > 0);

    const adds: Array<OrderAddItemV2> = [];

    outLocations.forEach(loc => {
      let idx = adds.findIndex(item => item.lot === loc.lotItem.lot.lot);
      if (idx === -1) {
        adds.push(
          {
            productId: loc.lotItem?.productId || '',
            count: loc.outCount,
            lot: loc.lotItem?.lot?.lot || '',
            thdlot: loc.lotItem?.lot?.thdLot || '',
            desc: loc.lotItem?.lot?.desc || '',
            sourceId: errItem.sourceId,
            sourceType: errItem.sourceType,
            locations: [{
              locationId: loc.locationId,
              count: loc.outCount,
              barcodes: []
            }]
          }
        )
      } else {
        adds[idx].count += loc.outCount
        adds[idx].locations = [
          ...adds[idx].locations,
          {
            locationId: loc.locationId,
            count: loc.outCount,
            barcodes: []
          }
        ]
      }
    });

    onConfirm(adds);
  };

  return (
    <Modal
      title={t('warehouse.oldItemInfo')}
      open={open}
      onCancel={onClose}
      width={1200}
      footer={
        <Flex gap={8} justify="flex-end">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="primary" onClick={handleConfirm}>{t('common.confirm')}</Button>
        </Flex>
      }
      destroyOnClose
    >
      <div style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* 上部分：errItems 表格 */}
        <Table
          columns={errItemColumns}
          dataSource={errItems}
          rowKey="id"
          pagination={false}
          size="small"
          bordered
          scroll={{ y: 400 }}
          style={{ marginBottom: 16 }}
        />

        {/* 下部分：locations 表格 */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <STableLayout
            loading={loading}
            headerChildren={
              <Flex style={{ flex: 1, marginTop: -10 }} justify='space-between'>
                <Typography.Title level={5} style={{ margin: 0 }}>{t('warehouse.fixOut')}</Typography.Title>
                <Flex gap={8} style={{ color: outQty !== (errItems[0]?.count || 0) ? "red" : undefined }}>
                  <span>{t('warehouse.outQuantity')}: {outQty}</span>
                  <span>/ {errItems[0]?.count || 0}</span>
                </Flex>
              </Flex>
            }
            headerSub={
              <Form.Item label={t("requirement.matchLocType")} style={{ width: "100%" }}>
                <SelectLocationType
                  mode="multiple"
                  style={{ flex: 1 }}
                  value={locTypes}
                  onChange={setLocTypes}
                  maxTagCount={1}
                />
              </Form.Item>
            }
            total={showData.length}
            columns={locationColumns as any}
            dataSource={showData}
            rowKey="id"
            pagination={false}
            locale={{
              emptyText: <Empty description={
                isReturn ? t('warehouse.noLotStock', { lot: errItems[0].lot || '' })
                  : t('warehouse.noCodeStock', { code: errItems[0]?.product?.code || '' })
              } />
            }}
            extHeight={100}
            scrollX={1000}
          />
        </div>
      </div>
    </Modal>
  );
};

export default FixOutBoundItems;
